/**
 * SeedKeywordSuggestions - Collapsible sortable table of AI-generated seed keywords.
 *
 * Presentational component that displays categorized, prioritized seed keyword
 * suggestions with rationale tooltips. Parent owns the mutation state.
 */

import React, { useState, useMemo } from 'react';
import { Sparkles, ChevronUp, ChevronDown, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { SeedKeywordAnalysis, SeedKeywordCategory, SeedKeywordPriority, FruitTier } from '@/types/contentPlanner';
import { FRUIT_TIER_LABELS, FRUIT_TIER_COLORS } from '@/types/contentPlanner';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SeedKeywordSuggestionsProps {
  suggestions: SeedKeywordAnalysis[];
  onSelectKeyword: (keyword: string) => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

type SuggestionSortField = 'keyword' | 'category' | 'priority' | 'fruit_tier' | 'volume';

interface SuggestionSortConfig {
  field: SuggestionSortField;
  direction: 'asc' | 'desc';
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const CATEGORY_CONFIG: Record<SeedKeywordCategory, { label: string; className: string }> = {
  service: { label: 'Service', className: 'text-coral border-coral/40 bg-coral-light' },
  problem: { label: 'Problem', className: 'text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40' },
  informational: { label: 'How-To', className: 'text-teal-dark border-teal/40 bg-teal-light' },
  competitor_gap: { label: 'Gap', className: 'text-purple-700 dark:text-purple-400 border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-950/40' },
};

const PRIORITY_ORDER: Record<SeedKeywordPriority, number> = {
  high: 0,
  medium: 1,
  low: 2,
};

const PRIORITY_STYLES: Record<SeedKeywordPriority, string> = {
  high: 'text-coral font-medium',
  medium: 'text-amber-700 dark:text-amber-400',
  low: 'text-muted-foreground',
};

const FRUIT_TIER_ORDER: Record<string, number> = { low: 0, mid: 1, high: 2 };

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const SortIcon: React.FC<{ field: SuggestionSortField; current: SuggestionSortConfig }> = ({
  field,
  current,
}) => {
  if (current.field !== field) return null;
  return current.direction === 'asc' ? (
    <ChevronUp className="inline h-3.5 w-3.5 ml-0.5" />
  ) : (
    <ChevronDown className="inline h-3.5 w-3.5 ml-0.5" />
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SeedKeywordSuggestions: React.FC<SeedKeywordSuggestionsProps> = ({
  suggestions,
  onSelectKeyword,
  isOpen,
  onOpenChange,
}) => {
  const hasMetrics = useMemo(
    () => suggestions.some((s) => s.fruit_tier != null),
    [suggestions],
  );

  const [sort, setSort] = useState<SuggestionSortConfig>({
    field: 'priority',
    direction: 'asc',
  });

  // Switch default sort to fruit_tier when metrics arrive
  const [hasAppliedMetricSort, setHasAppliedMetricSort] = useState(false);
  React.useEffect(() => {
    if (hasMetrics && !hasAppliedMetricSort) {
      setSort({ field: 'fruit_tier', direction: 'asc' });
      setHasAppliedMetricSort(true);
    }
  }, [hasMetrics, hasAppliedMetricSort]);

  const handleSort = (field: SuggestionSortField) => {
    setSort((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const sortedSuggestions = useMemo(() => {
    const items = [...suggestions];
    items.sort((a, b) => {
      // Already-researched items always at bottom
      if (a.already_researched !== b.already_researched) {
        return a.already_researched ? 1 : -1;
      }

      let aVal: string | number;
      let bVal: string | number;

      switch (sort.field) {
        case 'keyword':
          aVal = a.keyword.toLowerCase();
          bVal = b.keyword.toLowerCase();
          break;
        case 'category':
          aVal = a.category;
          bVal = b.category;
          break;
        case 'priority':
          aVal = PRIORITY_ORDER[a.priority] ?? 1;
          bVal = PRIORITY_ORDER[b.priority] ?? 1;
          break;
        case 'fruit_tier':
          aVal = a.fruit_tier ? FRUIT_TIER_ORDER[a.fruit_tier] ?? 3 : 3;
          bVal = b.fruit_tier ? FRUIT_TIER_ORDER[b.fruit_tier] ?? 3 : 3;
          break;
        case 'volume':
          aVal = a.volume ?? -1;
          bVal = b.volume ?? -1;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sort.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sort.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return items;
  }, [suggestions, sort]);

  if (suggestions.length === 0) return null;

  return (
    <Collapsible open={isOpen} onOpenChange={onOpenChange}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center justify-between p-3 bg-teal-light border border-teal/30 rounded-lg hover:bg-teal/10 transition-colors"
        >
          <span className="flex items-center gap-1.5 text-sm font-medium text-teal-dark">
            <Sparkles className="h-3.5 w-3.5" />
            AI Suggestions ({suggestions.length})
          </span>
          {isOpen ? (
            <ChevronUp className="h-4 w-4 text-teal-dark" />
          ) : (
            <ChevronDown className="h-4 w-4 text-teal-dark" />
          )}
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="mt-1 border border-teal/20 rounded-lg overflow-hidden">
          <TooltipProvider delayDuration={300}>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted">
                  <TableHead
                    className="cursor-pointer select-none text-xs py-2 px-3"
                    onClick={() => handleSort('keyword')}
                  >
                    Keyword
                    <SortIcon field="keyword" current={sort} />
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none text-xs py-2 px-2 w-20"
                    onClick={() => handleSort('category')}
                  >
                    Category
                    <SortIcon field="category" current={sort} />
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none text-xs py-2 px-2 w-20 text-center"
                    onClick={() => handleSort('priority')}
                  >
                    Priority
                    <SortIcon field="priority" current={sort} />
                  </TableHead>
                  {hasMetrics && (
                    <>
                      <TableHead
                        className="cursor-pointer select-none text-xs py-2 px-2 w-20"
                        onClick={() => handleSort('fruit_tier')}
                      >
                        Fruit
                        <SortIcon field="fruit_tier" current={sort} />
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none text-xs py-2 px-1 w-16 text-right"
                        onClick={() => handleSort('volume')}
                      >
                        Vol
                        <SortIcon field="volume" current={sort} />
                      </TableHead>
                      <TableHead className="text-xs py-2 px-1 w-12 text-right">
                        KD
                      </TableHead>
                    </>
                  )}
                  <TableHead className="text-xs py-2 px-2 w-16 text-right">
                    Action
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedSuggestions.map((suggestion, i) => {
                  const catConfig =
                    CATEGORY_CONFIG[suggestion.category] || CATEGORY_CONFIG.informational;

                  return (
                    <TableRow
                      key={i}
                      className={cn(
                        'transition-colors',
                        suggestion.already_researched
                          ? 'opacity-50'
                          : 'cursor-pointer hover:bg-teal-light/50',
                      )}
                      onClick={() => {
                        if (!suggestion.already_researched) {
                          onSelectKeyword(suggestion.keyword);
                        }
                      }}
                    >
                      <TableCell className="py-2 px-3">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-sm font-medium truncate block max-w-[260px]">
                              {suggestion.keyword}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs">
                            <p className="font-medium text-sm">{suggestion.keyword}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {suggestion.rationale}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell className="py-2 px-2">
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-[10px] px-1.5 py-0 h-4 shrink-0',
                            catConfig.className,
                          )}
                        >
                          {catConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-2 px-2 text-center">
                        <span
                          className={cn(
                            'text-xs capitalize',
                            PRIORITY_STYLES[suggestion.priority] || 'text-muted-foreground',
                          )}
                        >
                          {suggestion.priority}
                        </span>
                      </TableCell>
                      {hasMetrics && (
                        <>
                          <TableCell className="py-2 px-2">
                            {suggestion.fruit_tier ? (
                              <Badge
                                variant="outline"
                                className={cn(
                                  'text-[10px] px-1.5 py-0 h-4 shrink-0',
                                  FRUIT_TIER_COLORS[suggestion.fruit_tier as FruitTier],
                                )}
                              >
                                {FRUIT_TIER_LABELS[suggestion.fruit_tier as FruitTier]}
                              </Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">–</span>
                            )}
                          </TableCell>
                          <TableCell className="py-2 px-1 text-right">
                            <span className="text-xs text-muted-foreground tabular-nums">
                              {suggestion.volume != null
                                ? suggestion.volume.toLocaleString()
                                : '–'}
                            </span>
                          </TableCell>
                          <TableCell className="py-2 px-1 text-right">
                            <span className="text-xs text-muted-foreground tabular-nums">
                              {suggestion.difficulty != null ? suggestion.difficulty : '–'}
                            </span>
                          </TableCell>
                        </>
                      )}
                      <TableCell className="py-2 px-2 text-right">
                        {suggestion.already_researched ? (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
                            <Check className="h-3 w-3" />
                            Done
                          </span>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs text-teal hover:text-teal-dark hover:bg-teal-light"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectKeyword(suggestion.keyword);
                            }}
                          >
                            Use
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TooltipProvider>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
